/**
 * Shape History Indicator (per-minute smile geometry time series;
 * displayed as the "Shape History" sub-toggle of IV Skew)
 *
 * Renders the fitted eSSVI slice's shape metrics as intraday lines in a
 * sub-pane below the main chart (chart.addPane(), shared time axis):
 *   SK  = ATM skew d(sigma)/dk (negative = downside strikes richer)
 *   CU  = ATM total-variance curvature (wing richness, both tails)
 *   FIT = fit RMSE in vol points (data-quality trace, dashed)
 * Replaces the former one-minute chip row: the crosshair reads exact
 * values at any minute and the PATH through the session is visible.
 *
 * Descriptive only: shape of what protection costs, not a direction
 * call (skew's predictive record is documented poor; see
 * test/RESEARCH-smile-levels.md).
 *
 * Data arrives pre-derived from GreeksHeatmapPlugin's onIvSkewShape
 * callback: { [sessionDate]: [{time, sk, cu, fit}] }, full session
 * hours (the smile rows cover ETH; ES genuinely moves overnight).
 * One series per metric per day so lines never connect across gaps.
 *
 * The pane MUST be created with chart.addPane(true) (preserveEmptyPane):
 * an empty emission removes every series, and the library otherwise
 * auto-removes the emptied pane, detaching this class's handle (all
 * later updates silently die; same defect crashed the Flow pane's
 * units switch 2026-08-11).
 */

import { LineSeries } from 'lightweight-charts';

const METRICS = ['sk', 'cu', 'fit'];

const SERIES_OPTIONS = {
  sk: {
    color: '#f59e0b', lineWidth: 2, title: 'SK',
  },
  cu: {
    color: '#8b5cf6', lineWidth: 2, title: 'CU',
  },
  fit: {
    color: 'rgba(148, 163, 184, 0.55)', lineWidth: 1, lineStyle: 2, title: 'FIT',
  },
};

export class IvSkewShapeIndicator {
  /**
   * @param {object} chart - The main lightweight-charts IChartApi instance
   * @param {object} pane  - An IPaneApi from chart.addPane()
   */
  constructor(chart, pane) {
    this.chart = chart;
    this.pane = pane;
    this.enabled = false;
    this._zeroPriceLine = null;
    this._segments = { sk: [], cu: [], fit: [] };
    this._lastSeries = null;
  }

  _removeSegments() {
    for (const key of METRICS) {
      for (const s of this._segments[key]) {
        try {
          this.chart.removeSeries(s);
        } catch (e) {
          // Series may already be detached during chart rebuild
        }
      }
      this._segments[key].length = 0;
    }
    this._zeroPriceLine = null;
  }

  _createSegment(key, isFirst) {
    return this.pane.addSeries(LineSeries, {
      priceScaleId: 'right',
      ...SERIES_OPTIONS[key],
      title: isFirst ? SERIES_OPTIONS[key].title : '',
      lastValueVisible: isFirst,
      priceLineVisible: false,
      crosshairMarkerVisible: true,
      priceFormat: {
        type: 'custom',
        formatter: (value) => (value == null || isNaN(value) ? '' : value.toFixed(2)),
      },
    });
  }

  enable(seriesByDay = null) {
    if (this.enabled) {
      if (seriesByDay) this.update(seriesByDay);
      return;
    }
    this.enabled = true;
    if (seriesByDay) this.update(seriesByDay);
    console.log('[IvSkewShapeIndicator] Enabled');
  }

  /**
   * Replace the pane content from the derived series map
   * { [sessionDate]: [{time, sk, cu, fit}] }. Days render as separate
   * segments in date order; FIT arrives in vol points already.
   */
  update(seriesByDay) {
    if (!this.enabled || !seriesByDay) return;
    this._lastSeries = seriesByDay;

    const days = Object.keys(seriesByDay).sort();
    const daySegments = days
      .map((d) => {
        const rows = (seriesByDay[d] || []).filter((r) => r && r.time != null);
        return {
          sk: rows.filter((r) => Number.isFinite(r.sk)).map((r) => ({ time: r.time, value: r.sk })),
          cu: rows.filter((r) => Number.isFinite(r.cu)).map((r) => ({ time: r.time, value: r.cu })),
          fit: rows.filter((r) => Number.isFinite(r.fit)).map((r) => ({ time: r.time, value: r.fit })),
        };
      })
      .filter((seg) => seg.sk.length || seg.cu.length || seg.fit.length);

    const numSegments = daySegments.length;
    if (numSegments === 0) {
      // Empty emission = the data context vanished (underlying/date
      // switch, fetch failure): clear rather than hold stale series.
      this._removeSegments();
      return;
    }

    if (this._segments.sk.length !== numSegments) {
      this._removeSegments();
      for (let i = 0; i < numSegments; i++) {
        for (const key of METRICS) {
          this._segments[key].push(this._createSegment(key, i === 0));
        }
      }
      // Zero reference for the skew sign on the first SK segment
      if (this._segments.sk.length > 0) {
        this._zeroPriceLine = this._segments.sk[0].createPriceLine({
          price: 0,
          color: '#666666',
          lineWidth: 1,
          lineStyle: 2,
          axisLabelVisible: false,
        });
      }
      try {
        this.pane.priceScale('right').applyOptions({
          visible: true,
          borderVisible: true,
          borderColor: '#2a2a2a',
          autoScale: true,
        });
      } catch (e) {
        console.warn('[IvSkewShapeIndicator] Price scale config error:', e);
      }
    }

    for (let i = 0; i < numSegments; i++) {
      for (const key of METRICS) {
        this._segments[key][i].setData(daySegments[i][key]);
      }
    }
  }

  disable() {
    if (!this.enabled) return;
    this._removeSegments();
    try {
      this.pane.priceScale('right').applyOptions({ visible: false });
    } catch (e) {
      // Price scale may not exist during chart teardown
    }
    this.enabled = false;
    console.log('[IvSkewShapeIndicator] Disabled');
  }

  isEnabled() {
    return this.enabled;
  }
}

export default IvSkewShapeIndicator;
